import { failure, success } from "./result";
import type { Result } from "./result";

/** Clock state of an employee (spec 6.4). */
export type ClockState = "Off" | "Working" | "OnBreak";

/** Kinds of clock events. */
export type ClockEventKind = "ClockIn" | "ClockOut" | "BreakStart" | "BreakEnd";

/**
 * Kind of a break, declared with the break start (§ 4 ArbZG, § 11 JArbSchG, ADR-0092): only a
 * `RestBreak` can be a statutory rest break; `Standby` (standby or waiting time) never is, and
 * `Unclassified` breaks (recorded without declaration) do not count either.
 */
export type BreakQualification = "Unclassified" | "RestBreak" | "Standby";

/** Error codes of the state machine (`docs/error-codes.md`). */
export const clockErrorCodes = {
  invalidTransition: "TT-1001",
  alreadyClockedIn: "TT-1002",
} as const;

/**
 * Applies an event to a clock state (INV-01, INV-02). Clock-out during a break ends the break.
 *
 * @param current - The current state.
 * @param kind - The event kind.
 * @returns The next state or a domain error.
 */
export function applyClockEvent(current: ClockState, kind: ClockEventKind): Result<ClockState> {
  let result: Result<ClockState>;

  if (current === "Off" && kind === "ClockIn") {
    result = success("Working");
  } else if (current === "Working" && kind === "BreakStart") {
    result = success("OnBreak");
  } else if (current === "OnBreak" && kind === "BreakEnd") {
    result = success("Working");
  } else if (current !== "Off" && kind === "ClockOut") {
    result = success("Off");
  } else if (current !== "Off" && kind === "ClockIn") {
    result = failure(
      clockErrorCodes.alreadyClockedIn,
      "A clock-in is not allowed while a working day is open.",
    );
  } else {
    result = failure(
      clockErrorCodes.invalidTransition,
      `Event '${kind}' is not allowed in clock state '${current}'.`,
    );
  }

  return result;
}

/**
 * Returns the events that are allowed in a state, in the order the UI offers them.
 *
 * @param state - The clock state.
 * @returns The allowed event kinds.
 */
export function allowedClockEvents(state: ClockState): readonly ClockEventKind[] {
  const allowed: Readonly<Record<ClockState, readonly ClockEventKind[]>> = {
    Off: ["ClockIn"],
    Working: ["BreakStart", "ClockOut"],
    OnBreak: ["BreakEnd", "ClockOut"],
  };
  const events = allowed[state];

  return events;
}
